import { createHash } from 'crypto';
import { format, parseISO } from 'date-fns';
import { calendar_v3 } from 'googleapis';

/**
 * Represents a meeting request that is sent to the owner.
 */
export type AppointmentProps = {
  /** Starting time string (in ISO format) */
  start: string;
  /** Ending time string (in ISO format) */
  end: string;
  /** Meeting title */
  summary: string;
  /** Meeting Identification number */
  meetId?: string;
  /** Email address of the requester. */
  calendarId: string;
  /** Course name to be used in the details for the invitation */
  courseName: string;
  /** Name of the requester */
  name: string;
  /** ID of the event created in Google Calendar */
  eventId?: string;
  /** Action which should be taken */
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  attendees: calendar_v3.Schema$EventAttendee[];
};

/**
 * Retrieves an access token from Google using a refresh token.
 *
 * @returns {Promise<string>} A promise that resolves to the access token.
 * @throws {Error} If any required environment variables are missing,
 *                 or if no access token is returned.
 */
export default async function getAccessToken(): Promise<string> {
  if (!process.env.NEXT_GOOGLE_CLIENT_SECRET) {
    throw new Error('NEXT_GOOGLE_CLIENT_SECRET not set');
  }
  if (!process.env.NEXT_GOOGLE_REFRESH_TOKEN) {
    throw new Error('NEXT_GOOGLE_REFRESH_TOKEN not set');
  }
  if (!process.env.NEXT_GOOGLE_CLIENT_ID) {
    throw new Error('NEXT_GOOGLE_CLIENT_ID not set');
  }

  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    client_secret: process.env.NEXT_GOOGLE_CLIENT_SECRET,
    refresh_token: process.env.NEXT_GOOGLE_REFRESH_TOKEN,
    client_id: process.env.NEXT_GOOGLE_CLIENT_ID,
  });

  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
    cache: 'no-cache',
  });

  const json = await response.json();

  if (!json.access_token) {
    throw new Error(
      `Couldn't get access token: ${JSON.stringify(json, null, 2)}`,
    );
  }

  return json.access_token as string;
}

function formatDateToISOString(dateStr: string): string {
  const parsedDate = parseISO(dateStr);

  const formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");

  return formattedDate;
}

export function buildEventBody({
  start,
  end,
  summary,
  calendarId,
  attendees,
  courseName,
}: AppointmentProps): calendar_v3.Schema$Event {
  const description = buildDescription(courseName);
  const hash = getHash(courseName + start);
  const body: calendar_v3.Schema$Event = {
    start: {
      dateTime: formatDateToISOString(start),
      timeZone: 'Europe/London',
    },
    end: {
      dateTime: formatDateToISOString(end),
      timeZone: 'Europe/London',
    },
    summary,
    description,
    attendees,
    conferenceData: {
      createRequest: {
        requestId: hash,
        conferenceSolutionKey: {
          type: 'hangoutsMeet',
        },
      },
    },

    reminders: {
      useDefault: false,
      overrides: [
        { method: 'email', minutes: 24 * 60 },
        { method: 'email', minutes: 30 },
        { method: 'popup', minutes: 10 },
      ],
    },
  };

  return body;
}

export async function createCalendarAppointment(props: AppointmentProps) {
  const body = buildEventBody(props);

  const apiUrl = new URL(
    'https://www.googleapis.com/calendar/v3/calendars/primary/events',
  );

  apiUrl.searchParams.set('sendNotifications', 'true');
  apiUrl.searchParams.set('conferenceDataVersion', '1');

  return fetch(apiUrl, {
    cache: 'no-cache',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getAccessToken()}`,
    },
    body: JSON.stringify(body),
  });
}

function buildDescription(courseName: string) {
  const baseDescription = `Hello,\n\n`;
  const meetDetails = `Details for Google Meet are attached, this meet is for ${courseName.trimEnd()}, please mark on your calendars and let us know if you can not attend.`;
  const closing = `\n\nSee you then!`;

  return baseDescription + meetDetails + closing;
}

/**
 * Generates a hash for the given data.
 *
 * This function takes a data string and generates a hash using the
 * SHA-256 algorithm. The hash is created by combining the input data
 * with the value of the GOOGLE_OAUTH_SECRET environment variable.
 *
 * @function
 * @param {string} data - The input data string for which to generate the hash.
 * @returns {string} The resulting hash as a hexadecimal string.
 */
export function getHash(data: string): string {
  return createHash('sha256').update(data).digest('hex');
}
